import { DateTime } from 'luxon';
import { RRule, RRuleSet } from 'rrule';
import { TIME_ZONE_UTC } from '@/constants';
import type { DecoratedEvent, DecoratedExperienceEvent } from '../types';
import { isExperienceEvent } from './event.lib';
import {
  fromRRuleDate,
  getFallbackUntil,
  getInstanceStart,
  getRecurrenceWindowAnchor,
  getRecurrenceZone,
  toInstanceDisplayStart,
  toRRuleAnchor,
} from './eventInstance.lib';
import {
  eventOverlapsWindow,
  type DateTimeRange,
  type DateTimeWindow,
} from './range.lib';

// An experience event's `metadata.hidden` is the list of occurrence start
// datetimes (ISO strings) hidden for this experience — one entry per hidden
// occurrence, so hiding one occurrence of a recurring event leaves the rest of
// the series visible. That is the same per-occurrence model the Schedule uses
// for `Entry.hidden` (see useUpdateEntry's hideEntry/unhideEntry, and
// getEntryInstances, which resolves each instance's own isHidden).
//
// `metadata.inheritedHidden` holds the occurrences hidden on the pages between
// this experience and the event's source (a page consumed as a feed shows its
// consumers what it shows, minus what it cleaned up). Those are not this page's
// to unhide, so they are treated as occurrences that do not exist: dropped by
// every expander whatever the reveal state, and never counted by the helpers
// that drive the "show hidden" control or the hide mutation. The server drops
// a one-time event hidden upstream altogether, so only series carry them.
//
// `getInstanceStart` owns the all-day/timed normalization, shared with event
// message instances so both key occurrences identically.

/** The stored identity of one occurrence. */
const toInstanceKey = (
  event: Pick<DecoratedExperienceEvent, 'isAllDay'>,
  instanceStart: DateTime
): DateTime =>
  getInstanceStart({ isAllDay: event.isAllDay, startDate: instanceStart });

/**
 * Whether the occurrence starting at `instanceStart` is hidden. The occurrence
 * is passed in rather than read off the event because the two are the same
 * object only for a non-recurring event: an expanded occurrence carries its own
 * `startDate`, while a series row (the edit modal, a Sources panel row) carries
 * the series start and names its occurrence separately.
 */
export const isInstanceHidden = (
  event: DecoratedExperienceEvent,
  instanceStart: DateTime
): boolean => {
  const key = toInstanceKey(event, instanceStart).toMillis();
  return event.metadata.hidden.some(
    (iso) => DateTime.fromISO(iso).toMillis() === key
  );
};

/**
 * Whether the occurrence starting at `instanceStart` was hidden upstream of
 * this experience. Unlike `isInstanceHidden`, this is not a state the page can
 * change, so no control reads it: only the expanders do, to leave the
 * occurrence out.
 */
export const isInstanceInheritedHidden = (
  event: DecoratedExperienceEvent,
  instanceStart: DateTime
): boolean => {
  const key = toInstanceKey(event, instanceStart).toMillis();
  return (event.metadata.inheritedHidden ?? []).some(
    (iso) => DateTime.fromISO(iso).toMillis() === key
  );
};

/**
 * Returns the next `hidden` array with one occurrence set to the given
 * visibility. The caller supplies the desired state (e.g. from optimistic UI)
 * rather than relying on this to read it back. Idempotent: any existing entry is
 * removed first, then re-added only when hiding — so it produces the correct
 * array regardless of stale/optimistic input and never duplicates.
 */
export const setInstanceHidden = (
  event: DecoratedExperienceEvent,
  instanceStart: DateTime,
  shouldHide: boolean
): string[] => {
  const start = toInstanceKey(event, instanceStart);
  const startMs = start.toMillis();
  const without = event.metadata.hidden.filter(
    (iso) => DateTime.fromISO(iso).toMillis() !== startMs
  );
  if (!shouldHide) {
    return without;
  }
  const iso = start.toISO();
  return iso ? [...without, iso] : without;
};

/** Whether any occurrence of the event is hidden. */
export const hasHiddenInstances = (event: DecoratedExperienceEvent): boolean =>
  event.metadata.hidden.length > 0;

/**
 * Whether a hidden occurrence falls inside `range` — what decides whether a view
 * offers its "show hidden" control. Occurrence keys are viewer-independent (an
 * all-day one is UTC midnight of its calendar date), so each is re-anchored in
 * the event's display zone before being compared against the range, which is
 * already in that zone.
 */
export const hasHiddenInstanceInRange = (
  event: DecoratedExperienceEvent,
  range?: DateTimeRange
): boolean => {
  if (!range) {
    return hasHiddenInstances(event);
  }

  const rangeStart = range.startDate.startOf('day');
  const rangeEnd = range.endDate.endOf('day');

  return event.metadata.hidden.some((iso) => {
    const start = toInstanceDisplayStart(
      DateTime.fromISO(iso, { zone: TIME_ZONE_UTC }),
      event.isAllDay,
      event.startDate.zoneName
    );
    return start >= rangeStart && start <= rangeEnd;
  });
};

/**
 * Whether one expanded occurrence should be rendered. Every expander calls this
 * for each occurrence it emits, which is what keeps a hidden occurrence out of
 * the view it was hidden in while the rest of its series stays.
 *
 * Magic-preview events carry no metadata and are always visible. `showHidden` is
 * the view's opt-in to render hidden occurrences anyway (the planner's
 * `?hidden=show`); it defaults to off so a surface that doesn't thread it keeps
 * hidden occurrences hidden rather than leaking them. It reveals only this
 * page's own hides: an occurrence hidden upstream stays out regardless.
 */
export const isInstanceVisible = (
  event: DecoratedEvent,
  instanceStart: DateTime,
  showHidden = false
): boolean => {
  if (!isExperienceEvent(event)) {
    return true;
  }
  if (isInstanceInheritedHidden(event, instanceStart)) {
    return false;
  }
  return showHidden || !isInstanceHidden(event, instanceStart);
};

/**
 * Drops only the events that have no visible occurrence left at all, so the
 * event-level list still stands for "what this experience shows".
 *
 * The two cases are deliberately different, and the asymmetry is the point:
 *
 * - A **non-recurring** event has exactly one occurrence — its own start — so
 *   hiding that occurrence leaves nothing to render, and the event goes. Every
 *   consumer of the list benefits, including the surfaces that never expand
 *   (page layouts, exports, the ListByEvent layout, category keys).
 * - A **recurring** event is always kept, whatever its `hidden` holds. Which of
 *   its occurrences are hidden can only be answered against a rule and a range,
 *   so it is answered per occurrence as each view expands the series
 *   (isInstanceVisible). Dropping the series here — the pre-#2841 behavior —
 *   would take every remaining occurrence with the one that was hidden.
 *
 * A surface that renders one row per series rather than one per occurrence
 * therefore keeps showing a recurring series with hidden occurrences. That is
 * the same thing the Schedule does, and the ListByEvent layout narrows it
 * further by testing the row's own occurrence.
 */
export const withoutFullyHiddenEvents = (
  events: DecoratedExperienceEvent[]
): DecoratedExperienceEvent[] =>
  events.filter((event) =>
    event.rrule
      ? true
      : !isInstanceHidden(event, event.startDate) &&
        !isInstanceInheritedHidden(event, event.startDate)
  );

type Options = {
  now?: DateTime;
  /** The view's reveal state, which decides whether hidden occurrences count. */
  showHiddenEvents?: boolean;
};

type EventInstanceSpan = {
  start: DateTime;
  end: DateTime;
};

// Days, not instants, and an exclusive all-day end, matching how the printable
// expander clips occurrences (see occurrenceInContent).
const instanceOverlapsWindow = (
  event: DecoratedEvent,
  instance: EventInstanceSpan,
  { start, end }: DateTimeWindow
): boolean => {
  const from = start?.toISODate() ?? null;
  const to = end?.toISODate() ?? null;
  if (from && to && from > to) {
    return false;
  }

  const firstDay = instance.start.toISODate();
  const lastDay = (
    event.isAllDay ? instance.end.minus({ days: 1 }) : instance.end
  ).toISODate();
  if (!firstDay || !lastDay) {
    return true;
  }
  return !(to && firstDay > to) && !(from && lastDay < from);
};

const getVisibleInstances = (
  event: DecoratedEvent,
  window: DateTimeWindow,
  { now = DateTime.utc(), showHiddenEvents = false }: Options = {}
): EventInstanceSpan[] => {
  if (!event.rrule) {
    return isInstanceVisible(event, event.startDate, showHiddenEvents)
      ? [{ start: event.startDate, end: event.endDate }]
      : [];
  }

  try {
    const recurrenceZone = getRecurrenceZone(event);
    const options = RRule.parseString(event.rrule);
    const dtstart = toRRuleAnchor(event.startDate, recurrenceZone);
    options.dtstart = dtstart.toJSDate();

    if (!options.until && !options.count) {
      options.until = getFallbackUntil({
        dtstart,
        windowAnchor: toRRuleAnchor(
          getRecurrenceWindowAnchor(now, {
            startDate: window.start,
            endDate: window.end,
          }),
          recurrenceZone
        ),
      }).toJSDate();
    }

    const rruleSet = new RRuleSet();
    rruleSet.rrule(new RRule(options));

    // An all-day span is a whole number of days and has to be re-added as days,
    // for the DST reason expandRecurringEvents documents.
    const duration = event.isAllDay
      ? { days: event.endDate.diff(event.startDate, 'days').days }
      : { minutes: event.endDate.diff(event.startDate, 'minutes').minutes };

    return rruleSet.all().flatMap((instanceDate) => {
      // Back into the display zone, as the expanders return their occurrences
      // and as the non-recurring branch above already does. instanceOverlapsWindow
      // compares calendar days, so an occurrence left in the recurrence zone is
      // judged against a different day than the one its card renders on.
      const start = fromRRuleDate(instanceDate, recurrenceZone).setZone(
        event.startDate.zone
      );
      return isInstanceVisible(event, start, showHiddenEvents)
        ? [{ start, end: start.plus(duration) }]
        : [];
    });
  } catch {
    // Exactly what the expander does with a rule it cannot parse: emit the
    // series start as the sole occurrence and still apply its visibility.
    return isInstanceVisible(event, event.startDate, showHiddenEvents)
      ? [{ start: event.startDate, end: event.endDate }]
      : [];
  }
};

/**
 * Whether an event actually puts something inside a window: the occurrence-level
 * answer to what `eventOverlapsWindow` answers at the series level.
 *
 * A series' span runs from its first occurrence to its last, so overlapping a
 * window is necessary but not sufficient: a Monday-only series overlaps a
 * Tuesday-to-Wednesday window and renders nothing in it. Callers that decide
 * whether the user can SEE an event have to ask this instead — an empty-state
 * overlay or an "it's off screen" explanation keyed on the span alone is wrong
 * exactly when a series straddles the window without landing in it.
 *
 * A rule that won't parse is judged by the series start: the same sole
 * occurrence the printable expander emits in that case.
 */
export const hasInstanceInWindow = (
  event: DecoratedEvent,
  window: DateTimeWindow,
  { now = DateTime.utc(), showHiddenEvents = false }: Options = {}
): boolean => {
  if (!eventOverlapsWindow(event, window)) {
    return false;
  }
  if (!window.start && !window.end && showHiddenEvents) {
    return true;
  }
  return getVisibleInstances(event, window, {
    now,
    showHiddenEvents,
  }).some((instance) => instanceOverlapsWindow(event, instance, window));
};

/** The events a window shows nothing of, occurrence by occurrence. */
export const getEventsWithoutInstanceInWindow = <E extends DecoratedEvent>(
  events: E[],
  window: DateTimeWindow,
  options?: Options
): E[] =>
  events.filter((event) => !hasInstanceInWindow(event, window, options));

/**
 * The nearest visible occurrence outside a window, preferring the first one
 * ahead of it and otherwise taking the last one behind it.
 */
export const getNearestInstanceOutsideWindow = (
  events: DecoratedEvent[],
  window: DateTimeWindow,
  options?: Options
): DateTime | undefined => {
  let ahead: DateTime | undefined;
  let behind: DateTime | undefined;

  for (const event of events) {
    for (const instance of getVisibleInstances(event, window, options)) {
      if (instanceOverlapsWindow(event, instance, window)) {
        continue;
      }
      if (window.end && instance.start > window.end) {
        if (!ahead || instance.start < ahead) {
          ahead = instance.start;
        }
      } else if (!behind || instance.start > behind) {
        behind = instance.start;
      }
    }
  }

  return ahead ?? behind;
};
